import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { TxHashBadge } from '../components/AddressBadge';
import { ArrowLeft, Layers, Clock, Hash, Database, Fuel, ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard } from '../lib/utils';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlockPage() {
  const { blockNumber } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/api/block/${blockNumber}`);
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch block:', err);
        setError('Failed to load block information');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [blockNumber]);

  const handleCopy = async (text, field) => {
    try {
      await copyToClipboard(text);
      setCopiedField(field);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const CopyButton = ({ text, field }) => (
    <button
      onClick={() => handleCopy(text, field)}
      className="p-1.5 rounded hover:bg-muted transition-colors"
      title="Copy to clipboard"
    >
      {copiedField === field ? (
        <Check className="w-4 h-4 text-emerald-500" />
      ) : (
        <Copy className="w-4 h-4 text-muted-foreground" />
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20 px-4">
        <h2 className="text-2xl font-bold mb-2">Block Not Found</h2>
        <p className="text-muted-foreground mb-4">{error || 'This block could not be found.'}</p>
        <Button onClick={() => navigate(-1)} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const blockNum = parseInt(blockNumber);

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in p-4" data-testid="block-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        className="mb-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Block Header */}
      <div className="border-b pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Layers className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Block #{blockNum.toLocaleString()}</h1>
              <p className="text-sm text-muted-foreground">Krown Chain</p>
            </div>
          </div>
          {/* Block Navigation */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/block/${blockNum - 1}`)}
              disabled={blockNum <= 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/block/${blockNum + 1}`)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Info Card */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="divide-y">
            {/* Block Number */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Layers className="w-4 h-4" />
                <span className="text-sm font-medium">Block Height:</span>
              </div>
              <div className="flex-1 font-mono">
                {blockNum.toLocaleString()}
              </div>
            </div>

            {/* Timestamp */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Timestamp:</span>
              </div>
              <div className="flex-1">
                {data.timestamp ? new Date(data.timestamp * 1000).toLocaleString() : '—'}
              </div>
            </div>

            {/* Block Hash */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Hash className="w-4 h-4" />
                <span className="text-sm font-medium">Block Hash:</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <code className="font-mono text-sm break-all">{data.hash}</code>
                <CopyButton text={data.hash} field="hash" />
              </div>
            </div>

            {/* Parent Hash */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Hash className="w-4 h-4" />
                <span className="text-sm font-medium">Parent Hash:</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Link 
                  to={`/block/${blockNum - 1}`}
                  className="font-mono text-sm text-primary hover:underline break-all"
                >
                  {data.parent_hash}
                </Link>
                <CopyButton text={data.parent_hash} field="parent" />
              </div>
            </div>

            {/* Transactions */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Database className="w-4 h-4" />
                <span className="text-sm font-medium">Transactions:</span>
              </div>
              <div className="flex-1">
                <Badge variant="outline">{data.transaction_count} transactions</Badge>
              </div>
            </div>

            {/* Gas Used */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Fuel className="w-4 h-4" />
                <span className="text-sm font-medium">Gas Used:</span>
              </div>
              <div className="flex-1 font-mono">
                {data.gas_used?.toLocaleString() || '—'} / {data.gas_limit?.toLocaleString() || '—'}
                {data.gas_used && data.gas_limit && (
                  <span className="text-muted-foreground ml-2">
                    ({((data.gas_used / data.gas_limit) * 100).toFixed(2)}%)
                  </span>
                )}
              </div>
            </div>

            {/* Miner/Validator */}
            {data.miner && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
                <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                  <Database className="w-4 h-4" />
                  <span className="text-sm font-medium">Validated By:</span>
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Link 
                    to={`/address/${data.miner}`}
                    className="font-mono text-sm text-primary hover:underline break-all"
                  >
                    {data.miner}
                  </Link>
                  <CopyButton text={data.miner} field="miner" />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Block Transactions */}
      {data.transactions && data.transactions.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b">
              <h2 className="font-semibold flex items-center gap-2">
                <Database className="w-4 h-4" />
                Block Transactions ({data.transactions.length})
              </h2>
            </div>
            <div className="divide-y max-h-[400px] overflow-y-auto">
              {data.transactions.map((tx, idx) => (
                <div key={idx} className="p-4 hover:bg-muted/30 flex items-center gap-4">
                  <span className="text-muted-foreground text-sm w-8">#{idx + 1}</span>
                  <TxHashBadge hash={tx} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
